
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { mapState } from "vuex";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";
import { downloadBlob } from "@/helpers/file";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { DefaultRevenueReportFilters } from "@/data/filters/reports";
import { BACKEND_DATE_FORMAT, BACKEND_DATETIME_FORMAT } from "@/data/date";
import { FilterOperators, FilterTypes } from "@/data/table";

//Types
import {
  IRevenueReportProvider,
  IRevenueReportProviderMethods
} from "@/models/providers/reports";
import { IState } from "@/store";
import { IFilter } from "@/models/tables";
import {
  IReportRow,
  IReportProductCategoryRow,
  IReportCashInRow
} from "@/models/reports";

@Component({
  computed: {
    ...mapState({
      revenueReport(state: IState, getters) {
        return getters[`data/list`]({
          storeModule: DataModules.REPORTS_REVENUE,
          scope: this.revenueReportScope
        });
      },
      revenueReportScope(state: IState, getters) {
        return getters[`data/${DataModules.REPORTS_REVENUE}/scope`];
      }
    })
  }
})
export default class RevenuReportProvider extends Mixins(RequestPromises) {
  // Map state
  public revenueReport!: any;
  public revenueReportScope!: string;

  // Props

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: false })
  public declare handleRequestError: boolean;

  @Prop({ default: () => DefaultRevenueReportFilters() })
  public availableFilters!: IFilter[];

  // Data

  public isLoading = true;
  public isReloading = false;
  public isProcessing = false;
  public hasRequestError = false;

  public activeFilters: IFilter[] = [];

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.activeFilters.push({
      key: "start_of_period",
      label: this.$t("_.reports.start_month"),
      operator: FilterOperators.AFTER,
      type: FilterTypes.MONTH,
      value: this.$moment().subtract(11, "months")
    });

    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.REPORTS_REVENUE,
      scope: this.revenueReportScope,
      vm: this
    });
  }

  // Methods

  public async loadData(ignoreStored = false) {
    try {
      await this.getRevenueReportData(ignoreStored);

      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
      this.isProcessing = false;
    }
  }

  public async reloadData() {
    this.isReloading = true;
    this.loadData(true);
  }

  public async refreshReport() {
    this.isProcessing = true;
    this.loadData(true);
  }

  public getRevenueReportData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.REPORTS_REVENUE}/get`, {
      scope: this.revenueReportScope,
      ...this.queryParams,
      vm: this,
      ignoreStored
    });
  }

  public onFiltersChange(newFilters) {
    this.$set(this, "activeFilters", newFilters);
    this.refreshReport();
  }

  public async regenerateReport() {
    try {
      this.isProcessing = true;

      await this.regenerateReportData();
      await this.getRevenueReportData(true);

      this.$store.dispatch("toast/show", {
        message: this.$t("_.reports.revenue_report_successfully_regenerated")
      });
      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public regenerateReportData() {
    return this.$store.dispatch(
      `data/${DataModules.REPORTS_REVENUE}/regenerateReport`
    );
  }

  public stringFormat(value) {
    if (!value) return "";
    return value.format(BACKEND_DATETIME_FORMAT);
  }

  public convertToCSV(objArray) {
    const array = typeof objArray != "object" ? JSON.parse(objArray) : objArray;
    let str = "";

    for (let i = 0; i < array.length; i++) {
      let line = "";
      for (const index in array[i]) {
        if (line != "") line += ",";

        line += array[i][index];
      }

      str += line + "\r\n";
    }

    return str;
  }

  public downloadCSV() {
    const blobData = this.revenueReportArray.splice(
      0,
      this.revenueReportArray.length - 1
    );
    const blob = new Blob([this.convertToCSV(blobData)], {
      type: "text/csv;charset=utf-8;"
    });

    downloadBlob(blob, `revenue_report_${this.reportCurrency}`);
  }

  public createDataRows(
    title,
    categories,
    totalTitle = "",
    totalKey = "",
    emptyRow = true
  ) {
    // Title
    const tbodyRows = [{ 0: title }];
    _.forEach(this.revenueReportMonths, (month, index) => {
      tbodyRows[0][index + 1] = " ";
    });

    // Body
    _.forEach(categories, category => {
      const row: any = { 0: category.label };
      _.forEach(this.revenueReportMonths, (month, index) => {
        row[index + 1] = this.findValue(month, category.valueKey);
      });

      tbodyRows.push(row);
    });

    // Total
    if (totalTitle) {
      const totalRow: any = { 0: totalTitle };
      _.forEach(this.revenueReportMonths, (month, index) => {
        totalRow[index + 1] = this.findValue(month, totalKey);
      });

      tbodyRows.push(totalRow);
    }

    // Empty row
    if (emptyRow) tbodyRows.push(this.emptyDataRow);

    return tbodyRows;
  }

  public getCategoryAmounts(
    categoryKey,
    rowLabel,
    columnLabel,
    showAmountFormatted = true
  ) {
    const revenueMonth = _.find(this.revenueReport?.months, (value, key) => {
      return columnLabel === key;
    });
    return {
      amount: revenueMonth?.[categoryKey]?.[rowLabel]?.["amount"],
      ...(showAmountFormatted
        ? {
            amountFormatted:
              revenueMonth?.[categoryKey]?.[rowLabel]?.["amount_formatted"] ||
              "–"
          }
        : {})
    };
  }

  public findValue(columnLabel, rowLabel) {
    const val = _.get(
      _.find(this.revenueReport?.months, (value, key) => {
        return columnLabel === key;
      }),
      rowLabel,
      "-"
    );
    return `"${val}"`; // Return in quotes to prevent CSV breaking when comma is in val
  }

  public findCashInValue(gateway, columnLabel, amountProp) {
    return _.get(
      _.find(this.revenueReport?.months, (value, key) => {
        return columnLabel === key;
      }),
      `${
        _.isNull(gateway.gatewayId)
          ? "cash_received_in_month_nogateway"
          : "cash_received_in_month_gateways"
      }${_.isNull(gateway.gatewayId) ? "" : `.${gateway.gatewayId}`}.${
        gateway.currencyLabel
      }.${amountProp}`,
      "-"
    );
  }

  // Computed

  public get queryParams() {
    if (_.isEmpty(this.activeFilters)) return {};

    const startOfPeriod = this.stringFormat(
      _.get(_.find(this.activeFilters, ["key", "start_of_period"]), "value")
    );
    const endOfPeriod = this.stringFormat(
      _.get(_.find(this.activeFilters, ["key", "end_of_period"]), "value")
    );

    return {
      params: {
        ...(startOfPeriod ? { start_of_period: startOfPeriod } : {}),
        ...(endOfPeriod ? { end_of_period: endOfPeriod } : {})
      }
    };
  }

  public get lastUpdated() {
    return _.get(
      this.revenueReport?.months,
      `${this.$moment()
        .startOf("month")
        .format(BACKEND_DATE_FORMAT)}.last_updated_datetime`,
      ""
    );
  }

  public get showRegenerateButton() {
    return _.keys(this.revenueReport).includes(
      this.$moment()
        .startOf("month")
        .format(BACKEND_DATE_FORMAT)
    );
  }

  public get revenueReportArray() {
    if (_.isEmpty(this.revenueReport)) return [];

    return [
      this.tableHeader,
      ...this.createDataRows(
        this.$t("_.reports.invoices_raised"),
        this.invoicesCategories,
        this.$t("_.reports.total_raised").toString(),
        "all_invoices_raised_formatted"
      ),
      ...this.cashInRows,
      ...this.createDataRows(
        this.$t("_.reports.imported_balances"),
        this.importedBalancesCategories
      ),
      ...this.createDataRows(
        this.$t("_.reports.accounts_receivable"),
        this.accountsCategories,
        this.$t("_.reports.accounts_receivable_end").toString(),
        "unpaid_at_end_of_period_formatted"
      ),
      ...this.createDataRows(
        this.$t("_.reports.revenue_recognition"),
        this.revenueCategories,
        this.$t("_.reports.recognised_revenue").toString(),
        "total_monthly_revenue_formatted"
      ),
      ...this.createDataRows(
        this.$t("_.clients"),
        this.clientsCategories,
        this.$t("_.reports.annual_churn").toString(),
        "annual_churn_rate"
      ),
      ...this.createDataRows(
        this.$t("_.reports.ltv"),
        this.ltvCategories,
        this.$t("_.reports.avg_ltv").toString(),
        "monthly_ltv_formatted"
      ),
      ...this.createDataRows(
        this.$t("_.reports.new_invoices"),
        this.newInvoicesCategories,
        this.$t("_.reports.all_invoices").toString(),
        "all_invoices_count"
      ),
      ...this.createDataRows(
        this.$t("_.reports.tickets"),
        this.ticketsCategories
      ),
      ...this.createDataRows(
        this.$t("_.reports.average_initial_order.initial_order"),
        this.averageInitialOrderCategories
      ),
      // By categories
      ...this.createCategoriesDataRows({
        tableTitle: this.$t("_.reports.revenue_by_product_category").toString(),
        tableCategories: this.productCategories,
        totalKey: "total_monthly_revenue_categories_sum_formatted",
        totalLabel: this.$t("_.reports.total_monthly_revenue").toString()
      }),
      ...this.createCategoriesDataRows({
        tableTitle: this.$t("_.reports.revenue_by_product_option").toString(),
        tableCategories: this.productOptions,
        totalKey: "total_monthly_revenue_options_sum_formatted",
        totalLabel: this.$t("_.reports.total_monthly_revenue").toString()
      }),
      ...this.createCategoriesDataRows({
        tableTitle: this.$t("_.reports.active_services_by_category").toString(),
        tableCategories: this.activeServicesCategories,
        totalKey: "active_services_categories_sum_formatted",
        totalLabel: this.$t("_.reports.total_active_services").toString()
      }),
      ...this.createCategoriesDataRows({
        tableTitle: this.$t(
          "_.reports.new_orders_amounts_by_category"
        ).toString(),
        tableCategories: this.newOrdersAmountsCategories,
        totalKey: "new_orders_amount_categories_sum_formatted",
        totalLabel: this.$t("_.reports.total_new_orders_amounts").toString()
      }),
      ...this.createCategoriesDataRows({
        tableTitle: this.$t(
          "_.reports.new_orders_amounts_by_option"
        ).toString(),
        tableCategories: this.newOrdersAmountsOptions,
        totalKey: "new_orders_amount_options_sum_formatted",
        totalLabel: this.$t("_.reports.total_new_orders_amounts").toString()
      }),
      ...this.createCategoriesDataRows({
        tableTitle: this.$t(
          "_.reports.new_orders_counts_by_category"
        ).toString(),
        tableCategories: this.newOrdersCountsCategories,
        totalKey: "new_orders_count_categories_sum_formatted",
        totalLabel: this.$t("_.reports.total_new_orders_counts").toString()
      }),
      ...this.createCategoriesDataRows({
        tableTitle: this.$t("_.reports.new_orders_counts_by_option").toString(),
        tableCategories: this.newOrdersCountsOptions,
        totalKey: "new_orders_count_options_sum_formatted",
        totalLabel: this.$t("_.reports.total_new_orders_counts").toString()
      }),
      ...this.createCategoriesDataRows({
        tableTitle: this.$t("_.reports.renew_rate_by_category").toString(),
        tableCategories: this.renewRateCategories
      }),
      ...this.createCategoriesDataRows({
        tableTitle: this.$t(
          "_.reports.renew_rate_by_option_category"
        ).toString(),
        tableCategories: this.renewRateOptions
      })
    ];
  }

  public get revenueReportMonths() {
    return _.keys(this.revenueReport?.months);
  }

  public get tableHeader() {
    const row = { 0: " " };

    _.forEach(this.revenueReportMonths, (month, index) => {
      row[index + 1] = month;
    });

    return row;
  }

  public get invoicesCategories(): IReportRow[] {
    return [
      {
        label: this.$t("_.reports.new_invoices").toString(),
        valueKey: "initial_invoices_raised_formatted"
      },
      {
        label: this.$t("_.reports.renewal_invoices").toString(),
        valueKey: "recurring_invoices_raised_formatted"
      },
      {
        label: this.$t("_.reports.upgrades_downgrades").toString(),
        valueKey: "migration_invoices_raised_formatted"
      },
      {
        label: this.$t("_.reports.credit_notes_refunds").toString(),
        valueKey: "credit_notes_invoices_raised_formatted"
      }
    ];
  }

  public get newInvoicesCategories(): IReportRow[] {
    return [
      {
        label: this.$t("_.reports.initial_invoices").toString(),
        valueKey: "initial_invoices_count"
      },
      {
        label: this.$t("_.reports.recurring_invoices").toString(),
        valueKey: "recurring_invoices_count"
      },
      {
        label: this.$t("_.reports.migration_invoices").toString(),
        valueKey: "migration_invoices_count"
      },
      {
        label: this.$t("_.reports.credit_notes").toString(),
        valueKey: "credit_notes_invoices_count"
      }
    ];
  }

  public get ticketsCategories(): IReportRow[] {
    return [
      {
        label: this.$t("_.reports.new_tickets").toString(),
        valueKey: "new_tickets"
      },
      {
        label: this.$t("_.reports.new_tickets_replies").toString(),
        valueKey: "total_replies_new_tickets",
        childCategories: [
          "total_replies_user_new_tickets",
          "total_replies_client_new_tickets"
        ]
      },
      {
        label: this.$t("_.reports.admin_new_tickets_replies").toString(),
        valueKey: "total_replies_user_new_tickets",
        parentCategory: "total_replies_new_tickets"
      },
      {
        label: this.$t("_.reports.client_new_tickets_replies").toString(),
        valueKey: "total_replies_client_new_tickets",
        parentCategory: "total_replies_new_tickets"
      },
      {
        label: this.$t("_.reports.total_replies").toString(),
        valueKey: "total_replies",
        childCategories: [
          "total_replies_user_new_tickets",
          "total_replies_client_new_tickets"
        ]
      },
      {
        label: this.$t("_.reports.admin_total_replies").toString(),
        valueKey: "total_replies_user",
        parentCategory: "total_replies"
      },
      {
        label: this.$t("_.reports.client_total_replies").toString(),
        valueKey: "total_replies_client",
        parentCategory: "total_replies"
      }
    ];
  }

  public get averageInitialOrderCategories(): IReportRow[] {
    return [
      {
        label: this.$t(
          "_.reports.average_initial_order.order_value"
        ).toString(),
        valueKey: "average_initial_order_value_formatted"
      },
      {
        label: this.$t(
          "_.reports.average_initial_order.product_value"
        ).toString(),
        valueKey: "average_initial_order_by_product_value_formatted"
      },
      {
        label: this.$t(
          "_.reports.average_initial_order.product_single_month_value"
        ).toString(),
        valueKey: "average_initial_order_by_product_bc_value_formatted"
      }
    ];
  }

  public get cashInCategories(): IReportCashInRow[] {
    return _.map(this.cashInRowsLabels, gateway => {
      return {
        currencyCode: gateway.currencyLabel,
        label: `${gateway.gatewayLabel}: ${gateway.currencyLabel}${
          gateway.currencyLabel === this.reportCurrency
            ? ` (${this.$t("_._default")})`
            : ""
        }`,
        columns: _.map(this.revenueReportMonths, columnLabel => {
          return {
            label: columnLabel,
            amount: this.findCashInValue(gateway, columnLabel, "amount"),
            amountConverted: this.findCashInValue(
              gateway,
              columnLabel,
              "amount_converted"
            ),
            amountConvertedFormatted: this.findCashInValue(
              gateway,
              columnLabel,
              "amount_converted_formatted"
            ),
            amountFormatted: this.findCashInValue(
              gateway,
              columnLabel,
              "amount_formatted"
            )
          };
        })
      };
    });
  }

  public get importedBalancesCategories(): IReportRow[] {
    return [
      {
        label: this.$t("_.reports.imported_wallets").toString(),
        valueKey: "cash_received_in_month_import_formatted"
      }
    ];
  }

  public get accountsCategories(): IReportRow[] {
    return [
      {
        label: this.$t("_.reports.accounts_receivable_start").toString(),
        valueKey: "unpaid_at_start_of_period_formatted"
      },
      {
        label: this.$t("_.reports.amounts_repayable_start").toString(),
        valueKey: "amounts_repayable_start_of_period_client_credit_formatted"
      },
      {
        label: this.$t("_.reports.invoices_raised").toString(),
        valueKey: "all_invoices_raised_formatted"
      },
      {
        label: this.$t("_.reports.sales_tax").toString(),
        valueKey: "taxes_formatted"
      },
      {
        label: this.$t("_.reports.currency_gain_loss").toString(),
        valueKey: "currency_gain_or_loss_formatted"
      },
      {
        label: this.$t("_.reports.total_cash_in").toString(),
        valueKey: "cash_received_in_month_formatted"
      },
      {
        label: this.$t("_.reports.amounts_repayable_end").toString(),
        valueKey: "amounts_repayable_end_of_period_client_credit_formatted"
      }
    ];
  }

  public get revenueCategories(): IReportRow[] {
    return [
      {
        label: this.$t("_.reports.deferred_balance_start").toString(),
        valueKey: "deferred_balance_at_start_of_period_formatted"
      },
      {
        label: this.$t("_.reports.invoices_raised").toString(),
        valueKey: "all_invoices_raised_formatted"
      },
      {
        label: this.$t("_.reports.recognised_immediately").toString(),
        valueKey: "of_which_revenue_recognised_formatted"
      },

      {
        label: this.$t("_.reports.deferred_addition").toString(),
        valueKey: "of_which_revenue_deferred_formatted"
      },
      {
        label: this.$t("_.reports.deferred_recognised").toString(),
        valueKey: "historic_deferred_recognised_formatted"
      },
      {
        label: this.$t("_.reports.deferred_balance_end").toString(),
        valueKey: "new_deferred_balance_at_end_of_period_formatted"
      }
    ];
  }

  public get clientsCategories(): IReportRow[] {
    return [
      {
        label: this.$t("_.reports.active_clients_start").toString(),
        valueKey: "active_clients_start"
      },
      {
        label: this.$t("_.reports.new_clients").toString(),
        valueKey: "active_clients_current_month"
      },
      {
        label: this.$t("_.reports.lost_clients").toString(),
        valueKey: "lost_clients"
      },

      {
        label: this.$t("_.reports.active_clients_end").toString(),
        valueKey: "active_clients_end"
      },
      {
        label: this.$t("_.reports.monthly_churn").toString(),
        valueKey: "monthly_churn_rate"
      }
    ];
  }

  public get ltvCategories(): IReportRow[] {
    return [
      {
        label: this.$t("_.reports.monthly_arpu").toString(),
        valueKey: "monthly_arpu_formatted"
      },
      {
        label: this.$t("_.reports.annual_arpu").toString(),
        valueKey: "annual_arpu_formatted"
      }
    ];
  }

  public createCategoriesDataRows({
    tableTitle,
    tableCategories,
    totalKey = "",
    totalLabel = ""
  }: {
    tableTitle: string;
    tableCategories: IReportProductCategoryRow[];
    totalKey?: string;
    totalLabel?: string;
  }) {
    // Title
    const tbodyRows = [{ 0: tableTitle }];
    _.forEach(this.revenueReportMonths, (month, index) => {
      tbodyRows[0][index + 1] = " ";
    });

    // Body
    _.forEach(tableCategories, category => {
      const row: any = { 0: category.label };

      if (category.columns)
        _.forEach(category.columns, ({ amount, amountFormatted }, index) => {
          row[index + 1] = `"${
            amountFormatted === undefined ? amount : amountFormatted ?? "–"
          }"`; // Return in quotes to prevent CSV breaking when comma is in val
        });
      else
        _.forEach(this.revenueReportMonths, (month, index) => {
          row[index + 1] = this.findValue(month, category.valueKey);
        });

      tbodyRows.push(row);
    });

    // Total

    if (totalKey) {
      const totalRow: any = { 0: totalLabel };

      _.forEach(this.revenueReportMonths, (month, index) => {
        totalRow[index + 1] = this.findValue(month, totalKey);
      });

      tbodyRows.push(totalRow);
    }

    tbodyRows.push(this.emptyDataRow);
    return tbodyRows;
  }

  public get cashInRows() {
    // Title
    const tbodyRows = [{ 0: this.$t("_.reports.cash_in") }];

    _.forEach(this.revenueReportMonths, (month, index) => {
      tbodyRows[0][index + 1] = " ";
    });

    // Body
    _.forEach(this.cashInCategories, category => {
      const row: any = { 0: category.label };
      _.forEach(category.columns, ({ amountFormatted }, index) => {
        row[index + 1] = `"${amountFormatted ?? "–"}"`; // Return in quotes to prevent CSV breaking when comma is in val
      });
      tbodyRows.push(row);
    });

    // Total
    const totalRow: any = { 0: this.$t("_.reports.total_cash_in") };
    _.forEach(this.revenueReportMonths, (month, index) => {
      totalRow[index + 1] = this.findValue(
        month,
        "cash_received_in_month_formatted"
      );
    });

    tbodyRows.push(totalRow);
    tbodyRows.push(this.emptyDataRow);

    return tbodyRows;
  }

  public get productCategories(): IReportProductCategoryRow[] {
    return _.orderBy(
      _.map(
        this.getCategoriesRowsLabels("total_monthly_revenue_categories"),
        ({ category_name, category_id }) => {
          return {
            label: category_name
              .replace('"', "")
              .replace(",", "")
              .replace("\n", ""),
            columns: _.map(this.revenueReportMonths, monthLabel => {
              return {
                label: monthLabel,
                ...this.getCategoryAmounts(
                  "total_monthly_revenue_categories",
                  category_id,
                  monthLabel
                )
              };
            })
          };
        }
      ),
      "label",
      "asc"
    );
  }

  public get productOptions(): IReportProductCategoryRow[] {
    return _.orderBy(
      _.map(
        this.getCategoriesRowsLabels("total_monthly_revenue_options"),
        ({ category_name, category_id }) => {
          return {
            label: category_name
              .replace('"', "")
              .replace(",", "")
              .replace("\n", ""),
            columns: _.map(this.revenueReportMonths, columnLabel => {
              return {
                label: columnLabel,
                ...this.getCategoryAmounts(
                  "total_monthly_revenue_options",
                  category_id,
                  columnLabel
                )
              };
            })
          };
        }
      ),
      "label",
      "asc"
    );
  }

  public get activeServicesCategories(): IReportProductCategoryRow[] {
    return _.concat(
      _.orderBy(
        _.map(
          this.getCategoriesRowsLabels("active_services_categories"),
          ({ category_name, category_id }) => {
            return {
              label: category_name
                .replace('"', "")
                .replace(",", "")
                .replace("\n", ""),
              columns: _.map(this.revenueReportMonths, monthLabel => {
                return {
                  label: monthLabel,
                  ...this.getCategoryAmounts(
                    "active_services_categories",
                    category_id,
                    monthLabel,
                    false
                  )
                };
              })
            };
          }
        ),
        "label",
        "asc"
      )
    );
  }

  public get newOrdersAmountsCategories(): IReportProductCategoryRow[] {
    return _.orderBy(
      _.map(
        this.getCategoriesRowsLabels("new_orders_amount_categories"),
        ({ category_name, category_id }) => {
          return {
            label: category_name
              .replace('"', "")
              .replace(",", "")
              .replace("\n", ""),
            columns: _.map(this.revenueReportMonths, monthLabel => {
              return {
                label: monthLabel,
                ...this.getCategoryAmounts(
                  "new_orders_amount_categories",
                  category_id,
                  monthLabel
                )
              };
            })
          };
        }
      ),
      "label",
      "asc"
    );
  }

  public get newOrdersAmountsOptions(): IReportProductCategoryRow[] {
    return _.orderBy(
      _.map(
        this.getCategoriesRowsLabels("new_orders_amount_options"),
        ({ category_name, category_id }) => {
          return {
            label: category_name
              .replace('"', "")
              .replace(",", "")
              .replace("\n", ""),
            columns: _.map(this.revenueReportMonths, columnLabel => {
              return {
                label: columnLabel,
                ...this.getCategoryAmounts(
                  "new_orders_amount_options",
                  category_id,
                  columnLabel
                )
              };
            })
          };
        }
      ),
      "label",
      "asc"
    );
  }

  public get newOrdersCountsCategories(): IReportProductCategoryRow[] {
    return _.orderBy(
      _.map(
        this.getCategoriesRowsLabels("new_orders_count_categories"),
        ({ category_name, category_id }) => {
          return {
            label: category_name
              .replace('"', "")
              .replace(",", "")
              .replace("\n", ""),
            columns: _.map(this.revenueReportMonths, monthLabel => {
              return {
                label: monthLabel,
                ...this.getCategoryAmounts(
                  "new_orders_count_categories",
                  category_id,
                  monthLabel,
                  false
                )
              };
            })
          };
        }
      ),
      "label",
      "asc"
    );
  }

  public get newOrdersCountsOptions(): IReportProductCategoryRow[] {
    return _.orderBy(
      _.map(
        this.getCategoriesRowsLabels("new_orders_count_options"),
        ({ category_name, category_id }) => {
          return {
            label: category_name
              .replace('"', "")
              .replace(",", "")
              .replace("\n", ""),
            columns: _.map(this.revenueReportMonths, columnLabel => {
              return {
                label: columnLabel,
                ...this.getCategoryAmounts(
                  "new_orders_count_options",
                  category_id,
                  columnLabel,
                  false
                )
              };
            })
          };
        }
      ),
      "label",
      "asc"
    );
  }

  public get renewRateCategories(): IReportProductCategoryRow[] {
    return _.concat(
      [
        {
          label: this.$t("_.reports.all_clients_renew_rate").toString(),
          valueKey: "renew_rate"
        } as IReportProductCategoryRow
      ],
      _.orderBy(
        _.map(
          this.getCategoriesRowsLabels("renew_rate_categories"),
          ({ category_name, category_id }) => {
            return {
              label: category_name
                .replace('"', "")
                .replace(",", "")
                .replace("\n", ""),
              columns: _.map(this.revenueReportMonths, monthLabel => {
                return {
                  label: monthLabel,
                  ...this.getCategoryAmounts(
                    "renew_rate_categories",
                    category_id,
                    monthLabel
                  )
                };
              })
            };
          }
        ),
        "label",
        "asc"
      )
    );
  }

  public get renewRateOptions(): IReportProductCategoryRow[] {
    return _.orderBy(
      _.map(
        this.getCategoriesRowsLabels("renew_rate_options"),
        ({ category_name, category_id }) => {
          return {
            label: category_name
              .replace('"', "")
              .replace(",", "")
              .replace("\n", ""),
            columns: _.map(this.revenueReportMonths, columnLabel => {
              return {
                label: columnLabel,
                ...this.getCategoryAmounts(
                  "renew_rate_options",
                  category_id,
                  columnLabel
                )
              };
            })
          };
        }
      ),
      "label",
      "asc"
    );
  }

  /** The BE aggregate all categories, from all months, and return them within
   * a convenient `report_keys` object which we can then iterate over. This
   * covers scenarios where categories may have been added only in later or
   * specific months. */

  public getCategoriesRowsLabels(key) {
    const aggregatedCategories = this.revenueReport?.report_keys?.[key] || {};
    return _.map(aggregatedCategories, (category, category_id) => ({
      ...category,
      category_id
    }));
  }

  public get emptyDataRow() {
    const emptyRow: any = { 0: " " };

    _.forEach(this.revenueReportMonths, (month, index) => {
      emptyRow[index + 1] = " ";
    });

    return emptyRow;
  }

  public get reportCurrency(): string {
    return _.get(
      this.revenueReport,
      `months[${this.revenueReportMonths[0]}]currency.code`,
      {}
    );
  }

  public get cashInRowsLabels() {
    let labels: any = [];

    /** The BE aggregate all gateways, from all months, and return them within a
     * convenient `report_keys` object which we can then iterate over. This
     * covers scenarios where gateways may have been enabled only in later or
     * specific months. */

    const aggregatedGateways =
      this.revenueReport?.report_keys?.cash_received_in_month_gateways || {};

    // Gateways

    _.forEach(aggregatedGateways, (gateway, gatewayId) => {
      _.forEach(gateway, (currency, currencyCode) => {
        labels.push({
          gatewayId,
          gatewayLabel: currency.gateway_name,
          currencyLabel: currencyCode
        });
      });
    });

    labels = _.orderBy(labels, "gatewayLabel", "asc");

    const aggregatedNoGateways =
      this.revenueReport?.report_keys?.cash_received_in_month_nogateway || {};

    // No gateways
    _.forEach(aggregatedNoGateways, (currency, currencyCode) => {
      labels.push({
        gatewayId: null,
        gatewayLabel: this.$t("_.reports.no_gateway"),
        currencyLabel: currencyCode
      });
    });

    return labels;
  }

  // Guarding

  public guardFunctionalities = ["get_reports_revenue"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $revenueReportData: this.providerData(),
        $revenueReportMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("revenueReportProviderData")
  public providerData(): IRevenueReportProvider {
    return {
      accountsCategories: this.accountsCategories,
      activeFilters: this.activeFilters,
      activeServicesCategories: this.activeServicesCategories,
      availableFilters: this.availableFilters,
      averageInitialOrderCategories: this.averageInitialOrderCategories,
      cashInCategories: this.cashInCategories,
      clientsCategories: this.clientsCategories,
      hasRequestError: this.hasRequestError,
      importedBalancesCategories: this.importedBalancesCategories,
      invoicesCategories: this.invoicesCategories,
      isLoading: this.isLoading,
      isProcessing: this.isProcessing,
      isReloading: this.isReloading,
      lastUpdated: this.lastUpdated,
      ltvCategories: this.ltvCategories,
      newOrdersAmountsCategories: this.newOrdersAmountsCategories,
      newOrdersAmountsOptions: this.newOrdersAmountsOptions,
      newInvoicesCategories: this.newInvoicesCategories,
      newOrdersCountsCategories: this.newOrdersCountsCategories,
      newOrdersCountsOptions: this.newOrdersCountsOptions,
      productCategories: this.productCategories,
      productOptions: this.productOptions,
      reportCurrency: this.reportCurrency,
      renewRateCategories: this.renewRateCategories,
      renewRateOptions: this.renewRateOptions,
      revenueCategories: this.revenueCategories,
      revenueReport: this.revenueReport,
      showRegenerateButton: this.showRegenerateButton,
      ticketsCategories: this.ticketsCategories
    };
  }

  @Provide("revenueReportProviderMethods")
  public get providerMethods(): IRevenueReportProviderMethods {
    return {
      downloadCSV: this.downloadCSV,
      onFiltersChange: this.onFiltersChange,
      refreshReport: this.refreshReport,
      regenerateReport: this.regenerateReport,
      reloadData: this.reloadData
    };
  }
}
